import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { RuleDocuments, DocPreviewDialog, PhotoControls } from "../components/PropertyFiles";
import { ArrowLeft, Trash2, CheckCircle2, AlertTriangle, XCircle, Circle, CalendarPlus, Bot, FileDown, Lock, RefreshCw, Link2, Trash, Mail, Loader2 } from "lucide-react";

function EmailReportButton({ pid, emailedAt, onSent }) {
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/properties/${pid}/report/email`);
      toast.success(`Report link emailed to ${data.to}`);
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the email");
    } finally { setSending(false); }
  };
  return (
    <button data-testid="email-report" onClick={send} disabled={sending}
      className="inline-flex items-center gap-2 border border-emerald-200 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg text-sm hover:bg-emerald-100 disabled:opacity-60 transition-colors">
      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email me this report
      {emailedAt && (
        <span data-testid="report-emailed-at" className="text-[11px] text-emerald-600/80">
          · sent {new Date(emailedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </span>
      )}
    </button>
  );
}

const STATUSES = [
  { key: "compliant", label: "Compliant", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "warning", label: "Warning", icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "non_compliant", label: "Non-compliant", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  { key: "not_started", label: "Not started", icon: Circle, cls: "bg-slate-50 text-slate-600 border-slate-200" },
];

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  const [docs, setDocs] = useState([]);
  const [preview, setPreview] = useState(null);

  const reload = () => api.get(`/properties/${id}`).then((r) => setData(r.data));
  const loadDocs = () => api.get(`/properties/${id}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { reload(); loadDocs(); }, [id]);

  const updateStatus = async (rule, statusKey) => {
    setSaving(rule.code);
    try {
      await api.post(`/properties/${id}/checklist`, { rule_code: rule.code, status: statusKey });
      toast.success(`${rule.title} → ${statusKey.replace("_", " ")}`);
      reload();
    } catch (e) {
      toast.error("Update failed");
    } finally { setSaving(null); }
  };

  const del = async () => {
    if (!confirm("Delete this property?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Deleted");
    nav("/app/properties");
  };

  const addRenewalDeadline = async (rule) => {
    if (!rule.renewal_months) return;
    const date = new Date();
    date.setMonth(date.getMonth() + rule.renewal_months);
    await api.post(`/deadlines`, {
      property_id: id,
      title: `Renew: ${rule.title}`,
      date: date.toISOString().slice(0, 10),
      rule_code: rule.code,
    });
    toast.success("Renewal scheduled");
    reload();
  };

  if (!data) return <div className="text-sm text-slate-500" data-testid="property-loading">Loading…</div>;

  const { property, city, checklist } = data;
  const byCode = Object.fromEntries(checklist.map((c) => [c.rule_code, c]));
  const total = city.rules.length;
  const compliant = city.rules.filter((r) => byCode[r.code]?.status === "compliant").length;
  const score = Math.round((compliant / total) * 100);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="property-detail">
      <Link to="/app/properties" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-properties">
        <ArrowLeft className="w-4 h-4" /> All properties
      </Link>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="aspect-[16/5] relative bg-slate-100">
          <img src={property.hero_image} alt="" className="w-full h-full object-cover" data-testid="property-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
          <PhotoControls property={property} onChanged={reload} />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="text-xs font-mono uppercase tracking-widest opacity-80">{city.name}, {city.state} · {city.ordinance}</div>
            <h1 className="text-3xl font-bold font-display mt-1">{property.name}</h1>
            <div className="text-sm opacity-90 mt-1">{property.address}</div>
          </div>
        </div>
        <div className="p-6 grid sm:grid-cols-4 gap-4">
          <Stat label="Compliance" value={`${score}%`} accent={score >= 80 ? "emerald" : score >= 50 ? "amber" : "rose"} />
          <Stat label="Bedrooms" value={property.bedrooms} />
          <Stat label="Max guests" value={property.max_occupancy} />
          <Stat label="Night cap" value={city.night_cap ? `${city.night_cap}/yr` : "—"} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Link to={`/app/assistant?property=${id}&city=${city.slug}`} data-testid="ask-ai-property" className="inline-flex items-center gap-2 bg-sky-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-sky-600 transition">
          <Bot className="w-4 h-4" /> Ask AI about this property
        </Link>
        {property.has_report ? (
          <button
            data-testid="download-report"
            onClick={async () => {
              const t = toast.loading("Generating your report…");
              try {
                const res = await api.get(`/properties/${id}/report.pdf`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `havenguard-${property.name.replace(/\s+/g, "_")}-${new Date().toISOString().slice(0,10)}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Report downloaded", { id: t });
              } catch (err) {
                toast.error(err.response?.data?.detail || "Download failed", { id: t });
              }
            }}
            className="inline-flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-600 transition">
            <FileDown className="w-4 h-4" /> Download compliance report
          </button>
        ) : null}
        {property.has_report ? (
          <EmailReportButton pid={id} emailedAt={property.report_emailed_at} onSent={reload} />
        ) : (
          <Link
            to="/app/billing"
            data-testid="unlock-report"
            className="inline-flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-amber-600 transition">
            <Lock className="w-4 h-4" /> Unlock compliance report · $29
          </Link>
        )}
        <button onClick={del} data-testid="delete-property" className="inline-flex items-center gap-2 text-rose-600 border border-rose-200 px-4 py-2 rounded-lg text-sm hover:bg-rose-50 transition">
          <Trash2 className="w-4 h-4" /> Delete property
        </button>
      </div>

      <ICalSyncCard property={property} onChanged={reload} />

      <div>
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-1">Compliance checklist</h2>
        <p className="text-sm text-slate-600 mb-4">{city.summary}</p>
        <div className="space-y-3">
          {city.rules.map((rule) => {
            const current = byCode[rule.code]?.status || "not_started";
            const cur = STATUSES.find((s) => s.key === current);
            return (
              <div key={rule.code} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`rule-${rule.code}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">{rule.code}</span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{rule.category}</span>
                      {rule.renewal_months && (
                        <button onClick={() => addRenewalDeadline(rule)} data-testid={`renewal-${rule.code}`} className="text-[10px] font-mono text-sky-600 hover:underline inline-flex items-center gap-1">
                          <CalendarPlus className="w-3 h-3" /> +{rule.renewal_months}mo renewal
                        </button>
                      )}
                    </div>
                    <div className="font-display font-semibold text-slate-900 mt-1">{rule.title}</div>
                    <div className="text-sm text-slate-600 mt-1">{rule.detail}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${cur.cls}`}>
                    <cur.icon className="w-3 h-3" /> {cur.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {STATUSES.map((s) => (
                    <button key={s.key} disabled={saving === rule.code} onClick={() => updateStatus(rule, s.key)}
                      data-testid={`status-${rule.code}-${s.key}`}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition ${current === s.key ? s.cls + " font-medium" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <RuleDocuments propertyId={id} ruleCode={rule.code} docs={docs.filter((d) => d.rule_code === rule.code)}
                  onChanged={loadDocs} onPreview={setPreview} />
              </div>
            );
          })}
        </div>
      </div>
      <DocPreviewDialog doc={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

function Stat({ label, value, accent }) {
  const map = { emerald: "text-emerald-600", amber: "text-amber-600", rose: "text-rose-600" };
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-2xl font-bold font-display mt-1 ${accent ? map[accent] : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function ICalSyncCard({ property, onChanged }) {
  const [url, setUrl] = useState(property.ical_url || "");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const hasUrl = Boolean(property.ical_url);

  const save = async () => {
    if (!url.trim().startsWith("https://")) { toast.error("Paste an https:// iCal feed URL"); return; }
    setSaving(true);
    try {
      await api.post(`/properties/${property.id}/ical`, { ical_url: url.trim() });
      toast.success("iCal feed saved");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const sync = async () => {
    setSyncing(true); setResult(null);
    try {
      const { data } = await api.post(`/properties/${property.id}/ical/sync`);
      setResult(data);
      toast.success(`Synced — ${data.imported} new, ${data.updated} updated`);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const remove = async () => {
    if (!confirm("Disconnect this iCal feed? Existing bookings stay put.")) return;
    await api.delete(`/properties/${property.id}/ical`);
    setUrl(""); setResult(null); toast.success("iCal feed disconnected");
    onChanged();
  };

  const src = hasUrl && (property.ical_url.toLowerCase().includes("airbnb") ? "Airbnb"
    : property.ical_url.toLowerCase().includes("vrbo") || property.ical_url.toLowerCase().includes("homeaway") ? "Vrbo"
    : property.ical_url.toLowerCase().includes("booking.com") ? "Booking.com" : "Custom");

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="ical-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-slate-500" />
            <h2 className="font-display text-lg font-semibold text-slate-900">Booking sync</h2>
            {hasUrl && <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">{src}</span>}
          </div>
          <p className="text-sm text-slate-600 mt-1">Paste your Airbnb / Vrbo / Booking.com iCal export URL — we'll import reservations and auto-schedule turnovers.</p>
        </div>
        {hasUrl && (
          <div className="text-right text-xs text-slate-500 font-mono">
            {property.ical_last_sync && <>Last sync {new Date(property.ical_last_sync).toLocaleString()}</>}
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <input
          data-testid="ical-url-input"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.airbnb.com/calendar/ical/1234567.ics?s=..."
          className="flex-1 min-w-[280px] px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm focus:border-sky-500 outline-none font-mono"
        />
        <button
          data-testid="ical-save"
          onClick={save}
          disabled={saving || url === property.ical_url}
          className="px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-40 transition">
          {saving ? "Saving…" : hasUrl ? "Update" : "Save"}
        </button>
        <button
          data-testid="ical-sync"
          onClick={sync}
          disabled={!hasUrl || syncing}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-40 transition">
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
        </button>
        {hasUrl && (
          <button data-testid="ical-remove" onClick={remove} title="Disconnect"
            className="px-3 py-2.5 rounded-lg text-slate-400 border border-slate-200 hover:text-rose-600 hover:border-rose-200 transition">
            <Trash className="w-4 h-4" />
          </button>
        )}
      </div>
      {result && (
        <div data-testid="ical-result" className="mt-3 text-xs font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 inline-flex flex-wrap gap-4">
          <span className="text-emerald-700"><b>{result.imported}</b> imported</span>
          <span className="text-sky-700"><b>{result.updated}</b> updated</span>
          <span className="text-slate-500"><b>{result.skipped}</b> skipped</span>
          <span className="text-slate-500">source: {result.source}</span>
        </div>
      )}
    </div>
  );
}
